// ============================================================
// TASK SYNC ENGINE (C3 拡張) — Firestore タスク ↔ Google Tasks 完全双方向同期（B案）。
//
// 各メンバーの「自分担当タスク（assignee == viewerId）」を、その人の Google ToDo の
// **デフォルト「マイタスク」リスト**（@default 相当）に **完全双方向** で同期する。
// 2sync(SaaS) を使わず自前実装。同期は「ポータルを開いている間（＝アクセストークンが
// ある間）」のみ。サーバー常駐の同期はやらない（将来拡張）。
//
// ── 完全双方向（B案）──────────────────────────────────────
//   ・ポータル発タスク（googleTaskId で紐付いたもの）→ Google にミラー＋編集を相互反映。
//   ・Google「マイタスク」リスト側で足した／既にあるタスク → ポータルに取り込む（Loop4）。
//     取り込んだタスクは全社共有（admin/役員も閲覧可）。混在回避のため専用リストを使う
//     方式は廃止し、デフォルトリスト＋取り込みで「Googleの自然な体験」を優先する。
//   ・冪等性：取り込んだタスクは次回 googleTaskId 付きで Loop3 に入り linkedGoogleIds に
//     登録される → Loop4 で二度と拾われない（＝二重取り込みされない）。
//
// ── 🔴 2026-07-26 重複タスク事故の対策（土居さんの報告：Google側に同じタスクが複数）──
//   原因は **アプリ→Google 作成が冪等でなかったこと**。「Google に作る → Firestore に
//   googleTaskId を書き戻す」の2段階で後段が落ちると、Google に孤児が残りアプリ側は
//   未同期のまま＝次の同期（クライアント／10分毎のサーバー）が **また作る**。加えて
//   listId 不一致時の delete→create（delete 失敗は無視）と `'@default'` フォールバックが
//   孤児を量産していた。対策は3つ：
//     ① 未同期タスクは作る前に「同名かつ未紐付けの Google タスク（孤児）」を拾って
//        **作らずに紐付ける**（takeOrphan）。書き戻しが落ちても次回に回収され増殖しない。
//     ② listId は実 id が取れないなら同期しない（google.ts getDefaultTasklistId が null）。
//     ③ listId 不一致は **記録の付け替えのみ**。同期のために作り直さない。
//   ①の前提として linkedGoogleIds は走査前に一括収集する（走査中に足すと孤児を誤判定）。
//
// ── 無限ループ防止の設計（維持）─────────────────────────────
// 各アプリタスクは Google 同期メタを持つ：
//   googleTaskId   … 紐付く Google タスク id
//   googleTaskListId … 紐付く Google タスクリスト id（移行検知に使う）
//   gtUpdated      … 最後に取り込んだ Google 側 `updated`（RFC3339）
//   syncedAt       … 最後に同期が成立した時刻（epoch ms）
//   updatedAt      … アプリ側の最終編集時刻（epoch ms・updateTask が毎回セット）
//
// 突合した1ペアの方向判定（上から評価・最初に当たった1方向だけ実行）：
//   1) pull  : Google.updated（ms化） >  gtUpdated（ms化）
//        → Google が前回同期後に変わった。アプリへ反映（title/due/完了）。
//          反映後、gtUpdated=Google.updated に更新。updateTask が updatedAt を
//          進めるので、その同じ値を syncedAt に入れて push 誤発火を防ぐ。
//   2) push  : app.updatedAt >  syncedAt（pull に当たらなかった場合のみ）
//        → アプリが前回同期後に変わった。Google へ反映。反映後の Google.updated を
//          gtUpdated に、syncedAt を app.updatedAt（同期成立時点の値）に更新。
//   3) skip  : どちらでもない → 何もしない（＝ループの停止点）。
//
// ループしない根拠（push 後に re-read で Google の `updated` を取り込むため、自分の
// 書き込みを次回 pull で読み戻さない）は従来通り。
//
// 例外・トークン失効（401）は握りつぶして部分成功で返す（UI を壊さない）。
// 秘密・トークンはログに出さない。
// ============================================================
import {
  createGoogleSyncTask,
  fetchGoogleTasksInList,
  getDefaultTasklistId,
  getGoogleSyncTask,
  setGoogleTaskCompleted,
  setGoogleTaskDue,
  updateGoogleTask,
  type GoogleSyncTask,
} from './google';
import type { TaskCreate, TaskPatch } from '../data/adapter';
import type { Task, TaskPhase } from '../types/models';
import { acquireSyncLock, releaseSyncLock } from './syncLock';

/** RFC3339 → epoch ms（不正値・空は 0）。pull 判定で Google.updated と gtUpdated を比較する。 */
function rfcToMs(rfc: string | null | undefined): number {
  if (!rfc) return 0;
  const ms = Date.parse(rfc);
  return Number.isNaN(ms) ? 0 : ms;
}

/** アプリ status が「完了」か。Google の completed(boolean) と対応付ける。 */
function isAppDone(status: TaskPhase): boolean {
  return status === 'done';
}

/**
 * Google の完了状態をアプリ status へ写すときの目標フェーズを決める。
 *   ・Google 完了 → アプリ 'done'
 *   ・Google 未完了 → アプリは「done 以外」を維持したい。現状が 'done' のときだけ 'todo' に戻す
 *     （中間フェーズ doing/review/hold/report は Google に保存先が無いので潰さない）。
 */
function appStatusForGoogleCompleted(current: TaskPhase, googleCompleted: boolean): TaskPhase {
  if (googleCompleted) return 'done';
  return current === 'done' ? 'todo' : current;
}

export interface SyncResult {
  created: number; // Google→アプリ で新規取り込んだ件数（Loop4）
  updated: number; // pull でアプリ側を更新した件数
  pulled: number; // = updated（Google→アプリ 反映）— 呼称用エイリアス
  pushed: number; // push で Google 側を更新した件数
  mirrored: number; // アプリ→Google で **新規作成** した件数（未紐付け→初回ミラー）
  /**
   * 未紐付けタスクを、同名の既存 Google タスク（孤児）に **作らずに紐付けた** 件数。
   * ここが増えているときは過去に「作成は成功／書き戻しは失敗」が起きていた証拠になる。
   */
  adopted: number;
  /** 記録していた listId を現在のリスト id に付け替えただけの件数（作り直しはしない）。 */
  relisted: number;
  /** トークン失効等で listId が取れず同期できなかったとき true（UI で「未接続」に倒す）。 */
  skippedNoList: boolean;
}

export interface SyncArgs {
  token: string | null | undefined;
  viewerId: string;
  /**
   * 認証 uid（ロックのキー）。ユーザー単位の同期ロック `syncLocks/{uid}` に使う。
   * クライアント同期とサーバー同期（scheduledSync）の同時実行を防ぐため、同期本体を
   * このロックで囲う。未指定・db 無し（DEV）ならロックはスキップして従来動作。
   */
  uid?: string | null;
  appTasks: Task[];
  /** adapter.updateTask 相当（呼び出し側が getAdapter 済みのものを渡す）。 */
  updateTask: (id: string, patch: TaskPatch) => Promise<Task>;
  /** adapter.createTask 相当（Google→アプリ 取り込み用・Loop4）。 */
  createTask: (input: TaskCreate) => Promise<Task>;
}

/**
 * 自分担当タスクと Google ToDo のデフォルト「マイタスク」リストを **完全双方向** 同期する。
 * 冪等・無限ループ防止（上のヘッダ参照）。返り値は方向別の件数サマリ。
 *
 * 完全双方向（B案）：ポータル発タスクは Google にミラーし、Google 側で足した／既にある
 * タスクもポータルに取り込む（Loop4）。取り込んだタスクは全社共有（admin/役員も閲覧可）。
 * 失敗は握りつぶして部分成功で返す（throw しない）。token 無し（DEV/未ログイン）は即 no-op。
 */
export async function syncMyTasksWithGoogle(args: SyncArgs): Promise<SyncResult> {
  const { token, viewerId, uid } = args;
  const skipResult: SyncResult = {
    created: 0,
    updated: 0,
    pulled: 0,
    pushed: 0,
    mirrored: 0,
    adopted: 0,
    relisted: 0,
    skippedNoList: true,
  };
  // DEV/未ログイン（token 無し）では同期を一切走らせない（既存挙動維持）。
  if (!token || !viewerId) {
    return skipResult;
  }

  // ── ユーザー単位の同期ロックを取得（クライアント⇄サーバーの同時実行防止）──
  //    別の同期（サーバーの scheduledSync 等）が進行中なら acquire は false → 何もせず
  //    スキップ（skippedNoList=true で「今回は同期していない」を表す）。次のトリガー or
  //    次の10分で拾う。uid 無し・db 無し（DEV）は acquire が true を返して素通し。
  const lockKey = uid || '';
  const locked = await acquireSyncLock(lockKey);
  if (!locked) {
    return skipResult; // 別同期が進行中 → 二重同期を避けてスキップ。
  }
  try {
    return await runSyncBody(args, token, viewerId);
  } finally {
    // ロックは必ず解放（本体で throw されても・成功しても）。失敗時も TTL 5分で自然解放。
    await releaseSyncLock(lockKey);
  }
}

/**
 * 同期本体（ロック取得後のみ実行）。方向判定・Loop3/Loop4・移行・completedAt:null 等の
 * ロジックは従来と一切変えていない（前後にロックを挟んだだけ）。
 */
async function runSyncBody(args: SyncArgs, token: string, viewerId: string): Promise<SyncResult> {
  const { appTasks, updateTask, createTask } = args;
  const result: SyncResult = {
    created: 0,
    updated: 0,
    pulled: 0,
    pushed: 0,
    mirrored: 0,
    adopted: 0,
    relisted: 0,
    skippedNoList: false,
  };

  // 1) 同期先＝デフォルト「マイタスク」リストの id を解決。取れなければ何もしない（落とさない）。
  const listId = await getDefaultTasklistId(token);
  if (!listId) {
    result.skippedNoList = true;
    return result;
  }

  // 2) そのリストの Google タスクを取得（完了・非表示も含む）。
  const googleTasks = await fetchGoogleTasksInList(token, listId);
  const googleById = new Map<string, GoogleSyncTask>();
  for (const g of googleTasks) googleById.set(g.id, g);

  // 自分担当タスクだけを対象（他人のタスクは同期しない＝混線防止）。
  const mine = appTasks.filter((t) => t.assignee === viewerId);

  // ポータル側が「持っている／作った」Google task id の集合（Loop4 の取り込み判定に使う）。
  // ⚠️ 走査しながら足すと「まだ見ていないタスクの googleTaskId」が抜け、下の孤児判定が
  //    誤爆する。**走査前に一括収集する**（旧実装は走査中に足していた）。
  const linkedGoogleIds = new Set<string>();
  for (const t of mine) {
    if (t.googleTaskId) linkedGoogleIds.add(t.googleTaskId);
  }

  // ── 孤児インデックス（重複タスク事故の対策①＝作成の冪等化）──────────────
  //   孤児＝このリストにあるがポータルのどのタスクにも紐付いていない Google タスク。
  //   旧実装は「Google に作る → Firestore に googleTaskId を書き戻す」の2段階で、後段が
  //   落ちると孤児が残りアプリ側は未同期のまま → **次の同期でまた作る**（10分毎に増殖）。
  //   そこで未同期タスクは作る前にここから同名の孤児を拾い、**作らずに紐付ける**。
  //   これで書き戻しが落ちても次回に回収され、増殖が止まる。
  const orphansByTitle = new Map<string, GoogleSyncTask[]>();
  for (const g of googleTasks) {
    if (linkedGoogleIds.has(g.id)) continue;
    const key = (g.title || '').trim();
    if (!key) continue;
    const bucket = orphansByTitle.get(key);
    if (bucket) bucket.push(g);
    else orphansByTitle.set(key, [g]);
  }
  /**
   * 同名の孤児を1件だけ取り出して消費する（無ければ null）。
   * 消費した id は linkedGoogleIds に入れる＝Loop4 で二重取り込みされない。
   * 同名が複数あっても1タスクにつき1件しか消費しない（残りは Loop4 が取り込む）。
   */
  const takeOrphan = (title: string): GoogleSyncTask | null => {
    const bucket = orphansByTitle.get((title || '').trim());
    const g = bucket?.shift();
    if (!g) return null;
    linkedGoogleIds.add(g.id);
    return g;
  };

  // 3) 自分担当アプリタスクを走査して push/pull/初回ミラー/移行を実行。
  for (const t of mine) {
    try {
      // ── 未同期 → まず同名の孤児を拾う（作らない）。無ければ新規作成 ──
      if (!t.googleTaskId) {
        const orphan = takeOrphan(t.title);
        if (orphan) {
          // 過去に「作成成功／書き戻し失敗」で残った Google タスクを回収して紐付ける。
          // ここで作ってしまうと同名タスクがもう1件増える＝重複の再生産になる。
          const now = Date.now();
          await updateTask(t.id, {
            googleTaskId: orphan.id,
            googleTaskListId: listId,
            gtUpdated: orphan.updated,
            syncedAt: now,
            updatedAt: now,
          });
          result.adopted++;
          continue;
        }
        const created = await createGoogleSyncTask(token, listId, {
          title: t.title,
          due: t.due || null,
          completed: isAppDone(t.status),
        });
        if (created) {
          const now = Date.now();
          await updateTask(t.id, {
            googleTaskId: created.id,
            googleTaskListId: listId,
            gtUpdated: created.updated,
            syncedAt: now,
            updatedAt: now,
          });
          // (a) 初回 mirror で作った新 id を linked に登録（Loop4 の二重取り込み防止）。
          linkedGoogleIds.add(created.id);
          result.mirrored++;
        }
        continue;
      }

      // ── listId の食い違い＝記録を現在値に付け替えるだけ（対策③）──────────────
      //    🔴 旧実装は「旧リストの Google タスクを削除 → デフォルトに作り直す」だった。
      //       これが重複の主犯のひとつ。`getDefaultTasklistId` が一度でも仮想 id
      //       `'@default'` に落ちると全タスクで食い違いが起き、delete 失敗（失敗は無視）
      //       の分だけ Google に残骸が積む。**同期のために作り直すことはしない。**
      //    現在のリストに同じ googleTaskId が在るなら、記録している listId を直すだけ。
      //    無ければ何もしない（下の突合で g 無し → skip。削除同期は従来どおり将来）。
      if (t.googleTaskListId && t.googleTaskListId !== listId) {
        if (googleById.has(t.googleTaskId)) {
          await updateTask(t.id, { googleTaskListId: listId });
          result.relisted++;
        }
      }

      // ── 紐付け済み → 対応 Google task を突合 ──
      const g = googleById.get(t.googleTaskId);
      if (!g) {
        // 対応 Google task が見つからない（Google 側で削除された等）→ v1 は何もしない
        // （マッピングは残す。削除同期は将来）。
        continue;
      }

      const googleMs = rfcToMs(g.updated);
      const gtUpdatedMs = rfcToMs(t.gtUpdated);
      const appUpdatedMs = t.updatedAt ?? 0;
      const syncedMs = t.syncedAt ?? 0;

      if (googleMs > gtUpdatedMs) {
        // ── pull：Google 側が前回同期後に変わった → アプリへ反映 ──
        const nextStatus = appStatusForGoogleCompleted(t.status, g.completed);
        const patch: TaskPatch = { gtUpdated: g.updated };
        if (g.title && g.title !== t.title) patch.title = g.title;
        const gDue = g.due || '';
        if (gDue !== (t.due || '')) patch.due = gDue;
        if (nextStatus !== t.status) patch.status = nextStatus;
        // 反映後 updatedAt が進む。同じ値を syncedAt に入れて push 誤発火を防ぐ。
        const now = Date.now();
        patch.updatedAt = now;
        patch.syncedAt = now;
        await updateTask(t.id, patch);
        result.updated++;
        result.pulled++;
        continue;
      }

      if (appUpdatedMs > syncedMs) {
        // ── push：アプリ側が前回同期後に変わった → Google へ反映 ──
        let ok = true;
        const titleChanged = t.title !== g.title;
        const dueChanged = (t.due || '') !== (g.due || '');
        if (titleChanged || dueChanged) {
          ok =
            (await updateGoogleTask(token, listId, t.googleTaskId, {
              ...(titleChanged ? { title: t.title } : {}),
              ...(dueChanged ? { due: t.due || null } : {}),
            })) && ok;
        } else if (dueChanged) {
          ok = (await setGoogleTaskDue(token, listId, t.googleTaskId, t.due || null)) && ok;
        }
        // 完了状態の push。
        const appDone = isAppDone(t.status);
        if (appDone !== g.completed) {
          ok = (await setGoogleTaskCompleted(token, listId, t.googleTaskId, appDone)) && ok;
        }
        // 書き込み後の最新 Google task を読み直して新しい `updated` を取り込む
        // （これをしないと、自分の push を次回 pull で読み戻してループに見える）。
        const after = await getGoogleSyncTask(token, listId, t.googleTaskId);
        const nextGtUpdated = after?.updated || g.updated;
        await updateTask(t.id, {
          gtUpdated: nextGtUpdated,
          // 同期成立時点のアプリ updatedAt を syncedAt に固定（その後アプリを触らない限り再 push しない）。
          syncedAt: appUpdatedMs,
          updatedAt: appUpdatedMs,
        });
        if (ok) result.pushed++;
        continue;
      }
      // ── どちらでもない → skip（ループの停止点）──
    } catch (err) {
      // 1件の失敗で全体を止めない（部分成功）。秘密は出さない。
      console.warn('[taskSync] タスク同期で例外（スキップ）', err instanceof Error ? err.message : err);
      continue;
    }
  }

  // 4) Loop4：デフォルト「マイタスク」リストの Google task で linkedGoogleIds に無いもの
  //    （＝ポータル発でない＝ユーザーが Google 側で持っている／足したタスク）→ アプリへ取り込む。
  //    取り込むと googleTaskId 付きの app task になり、次回は Loop3 (c) で linked に入る
  //    → 二度と Loop4 に拾われない（＝二重取り込みされない・冪等）。
  for (const g of googleTasks) {
    if (linkedGoogleIds.has(g.id)) continue;
    try {
      const now = Date.now();
      await createTask({
        title: g.title,
        assignee: viewerId,
        status: g.completed ? 'done' : 'todo',
        due: g.due || '',
        projectId: null, // 未分類（Google 由来はPJ未確定）
        // ⚠️ 取り込んだ done タスクは completedAt を持たせない（null を明示）。
        //    Google 由来の過去完了を「今月ポータルで完了」に数えない＝monthlyStats 水増し防止。
        completedAt: null,
        googleTaskId: g.id,
        googleTaskListId: listId,
        gtUpdated: g.updated,
        syncedAt: now,
        updatedAt: now,
      });
      result.created++;
    } catch (err) {
      console.warn('[taskSync] Google→アプリ 取り込みで例外（スキップ）', err instanceof Error ? err.message : err);
      continue;
    }
  }

  return result;
}
